<template>
  <div
    v-if="state.requestModal.isOpen"
    class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
  >
    <div
      class="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto m-4 transform transition-all"
      @click.stop
    >
      <!-- 头部信息 -->
      <div
        class="relative bg-gradient-to-br from-primary/10 to-secondary/10 p-6 md:p-8"
      >
        <button
          @click="closeModal"
          class="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center text-neutral-600 hover:bg-white hover:text-neutral-900 transition-colors"
        >
          <i class="fa fa-times"></i>
        </button>

        <div class="flex items-start gap-3 mb-4">
          <span
            class="px-3 py-1 text-xs rounded-full"
            :class="{
              'bg-yellow-100 text-yellow-700': request.status === 0,
              'bg-green-100 text-green-700': request.status === 1,
            }"
          >
            {{ getStatusText(request.status) }}
          </span>
          <span
            class="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full"
          >
            {{ getCategoryName(request.categoryId) }}
          </span>
        </div>

        <h2 class="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
          {{ request.title }}
        </h2>

        <div class="flex items-center gap-4 text-sm text-neutral-500">
          <div class="flex items-center gap-2">
            <img
              :src="request.avatar || 'https://picsum.photos/id/64/100/100'"
              alt="头像"
              class="w-8 h-8 rounded-full"
            />
            <span>{{ request.uname || "匿名用户" }}</span>
          </div>
          <span>·</span>
          <span>{{ new Date(request.createTime).format("yyyy-MM-dd") }}</span>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="p-6 md:p-8">
        <!-- 需求描述 -->
        <div class="mb-8">
          <h3
            class="text-lg font-semibold text-neutral-900 mb-3 flex items-center"
          >
            <i class="fa fa-file-text-o text-primary mr-2"></i>需求描述
          </h3>
          <div
            class="bg-neutral-50 rounded-lg p-4 text-neutral-600 whitespace-pre-line leading-relaxed"
          >
            {{ request.content }}
          </div>
        </div>

        <!-- 联系方式 -->
        <div v-if="request.phone" class="mb-8">
          <h3
            class="text-lg font-semibold text-neutral-900 mb-3 flex items-center"
          >
            <i class="fa fa-phone text-primary mr-2"></i>联系方式
          </h3>
          <div class="bg-primary/5 rounded-lg p-4 text-neutral-600">
            {{ request.phone }}
          </div>
        </div>

        <!-- 评论区域 -->
        <div class="border-t border-neutral-200 pt-6">
          <h3
            class="text-lg font-semibold text-neutral-900 mb-4 flex items-center"
          >
            <i class="fa fa-comments-o text-primary mr-2"></i>
            评论交流
            <span class="ml-2 text-sm text-neutral-500"
              >({{ commentList.length }})</span
            >
          </h3>

          <!-- 评论输入框 -->
          <div class="mb-6 p-4 bg-neutral-50 rounded-lg">
            <textarea
              v-model="commentText"
              class="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 resize-none"
              rows="3"
              placeholder="分享您的想法或推荐合适的工具..."
            ></textarea>
            <div class="mt-3 flex justify-between items-center">
              <span class="text-xs text-neutral-400">文明发言，友善交流</span>
              <button
                @click="submitComment"
                :disabled="!commentText.trim() || submitting"
                class="px-6 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 transition-colors disabled:bg-neutral-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <i v-if="submitting" class="fa fa-spinner fa-spin"></i>
                <span>{{ submitting ? "提交中..." : "发表评论" }}</span>
              </button>
            </div>
          </div>

          <!-- 评论列表 -->
          <div v-if="commentList.length > 0" class="space-y-4">
            <div
              v-for="item in commentList"
              :key="item.id"
              class="flex items-start gap-3 p-4 bg-white border border-neutral-100 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              <img
                :src="item.avatar || 'https://picsum.photos/id/64/100/100'"
                alt="头像"
                class="w-10 h-10 rounded-full flex-shrink-0"
              />
              <div class="flex-1">
                <div class="flex justify-between items-start mb-1">
                  <h4 class="font-medium text-neutral-900">
                    {{ item.uname || "匿名用户" }}
                  </h4>
                  <span class="text-xs text-neutral-400">
                    {{ new Date(item.createTime).format("yyyy-MM-dd hh:mm") }}
                  </span>
                </div>
                <p class="text-neutral-600 text-sm whitespace-pre-line">
                  {{ item.content }}
                </p>
              </div>
            </div>

            <!-- 分页 -->
            <div v-if="commentTotal > pageSize" class="flex justify-center pt-4">
              <el-pagination
                background
                layout="prev, pager, next"
                :total="commentTotal"
                :page-size="pageSize"
                :current-page="commentPage"
                @current-change="handlePageChange"
              />
            </div>
          </div>

          <!-- 空状态 -->
          <div v-else class="text-center py-8">
            <div
              class="w-16 h-16 mx-auto mb-4 rounded-full bg-neutral-100 flex items-center justify-center"
            >
              <i class="fa fa-comment-o text-2xl text-neutral-400"></i>
            </div>
            <h4 class="text-neutral-600 font-medium mb-1">暂无评论</h4>
            <p class="text-sm text-neutral-400">成为第一个评论的人吧</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { globalState } from "../state";
import { ElMessage } from "element-plus";
import { requireLogin } from "../utils/authGuard";

export default {
  name: "RequestDetailModal",
  data() {
    return {
      state: globalState,
      request: {},
      commentList: [],
      commentTotal: 0,
      commentPage: 1,
      pageSize: 10,
      commentText: "",
      submitting: false,
    };
  },
  computed: {
    requestId() {
      return this.state.requestModal.requestId;
    },
    categories() {
      return this.state.requestModal.categories;
    },
    user() {
      return this.$store.state.user;
    },
    isLogin() {
      return this.user && this.user.uid;
    },
  },
  watch: {
    requestId(newVal) {
      if (newVal) {
        this.getRequestDetail();
        this.getComments();
      }
    },
  },
  mounted() {
    if (this.requestId) {
      this.getRequestDetail();
      this.getComments();
    }
  },
  methods: {
    async getRequestDetail() {
      try {
        const res = await this.$axios({
          url: `demand/${this.requestId}`,
          method: "GET",
        });
        if (res.code === 200) {
          this.request = res.data || {};
        }
      } catch (error) {
        console.error("获取需求详情失败:", error);
      }
    },

    async getComments() {
      try {
        var data = await this.$aesEncrypt({
          page: this.commentPage,
          pageSize: this.pageSize,
        });
        const res = await this.$axios({
          url: `demand/comment/list/${this.requestId}`,
          method: "POST",
          data: {
            data: data,
          },
        });
        if (res.code === 200) {
          this.commentList = res.data.list || [];
          this.commentTotal = res.data.total || 0;
        }
      } catch (error) {
        console.error("获取评论列表失败:", error);
        this.commentList = [];
        this.commentTotal = 0;
      }
    },

    // 分页切换
    handlePageChange(page) {
      this.commentPage = page;
      this.getComments();
    },

    async submitComment() {
      if (!requireLogin(this, "发表评论")) return;

      if (!this.commentText.trim()) {
        ElMessage.warning("请输入评论内容");
        return;
      }

      this.submitting = true;

      try {
        const data = await this.$aesEncrypt({
          demandId: this.requestId,
          content: this.commentText.trim(),
        });

        const res = await this.$axios({
          url: "demand/comment",
          method: "PUT",
          data: { data },
        });

        if (res.code === 200) {
          ElMessage.success("评论成功");
          this.commentText = "";
          // 如果当前在第一页，刷新评论列表；否则跳转到第一页查看最新评论
          if (this.commentPage === 1) {
            this.getComments();
          } else {
            this.commentPage = 1;
            this.getComments();
          }
          // 更新回复数
          this.request.replyCount = (this.request.replyCount || 0) + 1;
        } else {
          ElMessage.error(res.msg || "评论失败");
        }
      } catch (error) {
        console.error("提交评论失败:", error);
        ElMessage.error("提交失败，请稍后重试");
      } finally {
        this.submitting = false;
      }
    },

    getStatusText(status) {
      const map = {
        0: "进行中",
        1: "已解决",
      };
      return map[status] || "未知状态";
    },

    getCategoryName(id) {
      const category = this.categories.find((c) => c.id === id);
      return category ? category.title : "未知分类";
    },

    closeModal() {
      this.state.closeRequestModal();
    },
  },
};
</script>
